import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';


function SalesManIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const history = useHistory(); // Access the navigation history

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');

    useEffect(() => {
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }

        // Token is not expired, fetch data
        fetchtableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    //handle search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };    
    

    const fetchtableData = async () => {
        fetchData('SalesManApi', `/${companyId}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setData(response.response_data);                    
                }
            })
            .catch((error) => {
                console.error('Error fetching Sales Man Details:', error);
                // Handle error, e.g., redirect to login if token is expired
                if (error.response && error.response.status === 401) {
                    logout();
                    // Redirect to login with the current location as a parameter
                    history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
                }
            });
    };

    const handleDelete = async () => {
        deleteData('SalesManApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchtableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('SalesManApi', `${itemId}`, token)
            .then(() => {
                fetchtableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    return (
        <div className="w-full max-w-sm container mt-2 mx-auto">
            <div className='w-full mb-2 flex'>
                <h5 className='block uppercase tracking-wide text-black productHead flex-grow linkcolor' style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}>Sales Man</h5>
                {hasCreateMasterPermission && (
                    <Button
                        variant="contained"
                        color="primary"
                        component={Link}
                        to="/SalesManCreate"
                        className="linkcolor"
                        style={{ marginLeft: '8px' }}
                    >
                        CREATE NEW
                    </Button>
                )}

            </div>

            <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                <SearchIcon onClick={toggleSearchInput} style={{ cursor: 'pointer' }} />
                {showSearchInput && (
                    <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={handleSearch}
                        style={{ marginLeft: '10px', width: '200px' }}
                    />
                )}
            </div>

            <div className='w-full mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ backgroundColor: 'teal' }}>
                            <TableRow>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '80%' }}>Sales Man Name</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '16px', padding: '7px', color: 'whitesmoke' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ backgroundColor: 'white' }}>
                            {data.map(item => (
                                <TableRow key={item.id} className={item.IsActive ? '' : 'inactiveRow'}>
                                    <TableCell sx={{ height: '25px', padding: '5px', width: '40%' }}>{item.Name}</TableCell>
                                    <TableCell sx={{ height: '25px', padding: '5px', width: '10%' }}>
                                        {hasEditMasterPermission && (
                                            <Link to={`/salesManEdit/${item.Id}`}>
                                                <IconButton>
                                                    <EditIcon />
                                                </IconButton>
                                            </Link>
                                        )}
                                        {hasDeleteMasterPermission && (
                                            <IconButton onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))}>
                                                {item.IsActive ? <DeleteIcon /> : <ActivateIcon />}
                                            </IconButton>
                                        )}

                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <Dialog
                            open={confirmDialogOpen}
                            onClose={closeConfirmDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    Are you sure you want to delete this record?
                                </DialogContentText>
                            </DialogContent>

                            <DialogActions>
                                <button
                                    onClick={closeConfirmDialog}
                                    style={{
                                        backgroundColor: '#dc3545',
                                        color: '#ffffff',
                                        border: '1px solid #dc3545',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        marginRight: '8px',
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    style={{
                                        backgroundColor: '#28a745',
                                        color: '#ffffff',
                                        border: '1px solid #28a745',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Confirm
                                </button>
                            </DialogActions>

                        </Dialog>

                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}

export default SalesManIndex;

